import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { CompareHelper } from '@barthauer/abc/core';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { DialogResult } from '@barthauer/abc/messages';
import { BasysMapFrameService } from '@barthauer/basys/map-frame';
import { Subscription } from 'rxjs';
import { AppService } from '../../services/app.service';
import { DataService } from '../../services/data.service';
import { FrameService } from '../../services/frame.service';
import { MobileResourceEventViewModel } from '../../viewModels/mobileData.resources.viewModel';
import { ResourceListService } from '../resource-list/resource-list.service';
import { DetailPageWorkMode, ResourcesConstants, ResourcesNavigationContext } from '../resources.model';
import { ResourcesService } from '../resources.service';

@Component({
    selector: 'resource-navigation',
    templateUrl: './resource-navigation.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./resource-navigation.component.scss'],
    standalone: false
})
export class ResourceNavigationComponent implements OnInit, OnDestroy {
  public res = ResourceSet.Empty;
  public resourcesNavigationContext: ResourcesNavigationContext;
  public needForPersistence: boolean;
  public allowAdding: boolean;
  public allowEditing: boolean;
  public allowDeleting: boolean;
  @Output() public onAllowDataChanging = new EventEmitter<boolean>();

  private subscriptions: Subscription[] = [];
  private allowNavigation: boolean;
  private enforceCancelButton: boolean;
  private workMode: DetailPageWorkMode;
  private indexBeforeAdd?: number;
  public get allowForwardNavigation(): boolean {
    return this.allowNavigation && !this.isNavDisabled('increment');
  }
  public get allowBackwardNavigation(): boolean {
    return this.allowNavigation && !this.isNavDisabled('decrement');
  }
  public get allowCanceling(): boolean {
    return this.needForPersistence || this.enforceCancelButton;
  }

  constructor(
    public frame: BasysMapFrameService,
    private frameService: FrameService,
    private resourcesService: ResourcesService,
    private resourceListService: ResourceListService,
    private resManService: ResourceManagerService,
    private dataService: DataService
  ) {
    this.subscriptions.push(
      resourcesService.onNavigationContextRefresh().subscribe((context) => {
        this.resourcesNavigationContext = context;
        if (context) {
          if (this.resourcesNavigationContext.isDirectSingleNavigation) {
            this.executeOnNextMacroTask(() => this.switchWorkMode(DetailPageWorkMode.DirectSingleNavigation));
          } else {
            //use this.allowNavigation to identify the first call
            if (this.allowNavigation === undefined) {
              if (!context.resourceEventGroup.existsAnyResourceEventToday) {
                this.executeOnNextMacroTask(() => {
                  this.add();
                  this.resourcesNavigationContext.discardResourceEventWhenNavigateBack = true;
                });
              } else {
                this.executeOnNextMacroTask(() => this.switchWorkMode(DetailPageWorkMode.Standard));
              }
            }
          }
        }
      })
    );

    this.subscriptions.push(
      this.dataService.pendingObjectsChanged$.subscribe((poc) => {
        if (poc.category === ResourcesConstants.PersistenceCategory_ResourceEvent) {
          this.needForPersistence = poc.hasAnyChanges;
        }
      })
    );
  }
  private executeOnNextMacroTask(func: () => void): void {
    setTimeout(() => {
      func();
    });
  }

  async ngOnInit() {
    this.res = await this.resManService.getResourceSet('abc-maintenance-opera');
  }

  ngOnDestroy() {
    console.log('ResourceNavigationComponent_destroy');
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
    this.frameService.dispose();
  }

  public navigateForward() {
    this.resourcesService.navigateContext(1);
  }
  public navigateBackward() {
    this.resourcesService.navigateContext(-1);
  }
  private navigateSpecific(index: number) {
    this.resourcesService.navigateContextSpecific(index);
  }

  public add(): void {
    this.switchWorkMode(DetailPageWorkMode.New);
    this.indexBeforeAdd = this.resourcesNavigationContext.resourceEventIndexInGroup;
    this.navigateSpecific(0);
    this.resourcesNavigationContext.resourceEventGroup.addNewResourceEvent();
  }

  public edit(): void {
    this.switchWorkMode(DetailPageWorkMode.Edit);
  }

  public save(): void {
    this.dataService.persistPendingChanges(
      ResourcesConstants.PersistenceCategory_ResourceEvent,
      (err) => (this.needForPersistence = true)
    );
    this.needForPersistence = false;
    this.switchToCurrentDefaultWorkMode();
    this.resourcesNavigationContext.discardResourceEventWhenNavigateBack = false;
  }

  public cancel(): void {
    if (this.workMode === DetailPageWorkMode.New) {
      this.delete(true);
      this.navigateSpecific(this.indexBeforeAdd);
    } else {
      this.dataService.revertPendingChanges(ResourcesConstants.PersistenceCategory_ResourceEvent);
      this.needForPersistence = false;
      this.switchToCurrentDefaultWorkMode();
    }
    this.resourcesNavigationContext.discardResourceEventWhenNavigateBack = false;
  }

  public async delete(silent?: boolean): Promise<void> {
    let result: DialogResult = DialogResult.Yes;
    if (!silent) {
      const msgQuestionDelete = (await this.res).get('Resources-ConfirmDeletionMessage');
      result = await this.frameService.showMessageBox(msgQuestionDelete);
    }
    if (result === DialogResult.Yes) {
      this.resourcesNavigationContext.resourceEventGroup.removeResourceEvent(
        this.resourcesNavigationContext.resourceEventIndexInGroup
      );
      if (this.resourcesNavigationContext.isDirectSingleNavigation) {
        this.resourcesService.navigateTo(AppService.Route_ResourceList);
      } else {
        this.switchWorkMode(DetailPageWorkMode.Standard);
      }
    }
  }

  private switchToCurrentDefaultWorkMode(): void {
    if (this.resourcesNavigationContext.isDirectSingleNavigation) {
      this.switchWorkMode(DetailPageWorkMode.DirectSingleNavigation);
    } else {
      this.switchWorkMode(DetailPageWorkMode.Standard);
    }
  }

  private switchWorkMode(workMode: DetailPageWorkMode): void {
    this.workMode = workMode;
    switch (workMode) {
      case DetailPageWorkMode.Standard:
        const hasCurrentResourceEvent: boolean = this.resourcesNavigationContext.resourceEvent ? true : false;
        this.allowNavigation = true;
        this.allowAdding = true;
        this.allowEditing = hasCurrentResourceEvent;
        this.enforceCancelButton = false;
        this.allowDeleting = hasCurrentResourceEvent;
        this.onAllowDataChanging.emit(false);
        break;
      case DetailPageWorkMode.New:
        this.allowNavigation = false;
        this.allowAdding = false;
        this.allowEditing = false;
        this.enforceCancelButton = true;
        this.allowDeleting = false;
        this.onAllowDataChanging.emit(true);
        break;
      case DetailPageWorkMode.Edit:
        this.allowNavigation = false;
        this.allowAdding = false;
        this.allowEditing = false;
        this.enforceCancelButton = true;
        this.allowDeleting = false;
        this.onAllowDataChanging.emit(true);
        break;
      case DetailPageWorkMode.DirectSingleNavigation:
        this.allowNavigation = false;
        this.allowAdding = false;
        this.allowEditing = true;
        this.enforceCancelButton = false;
        this.allowDeleting = true;
        this.onAllowDataChanging.emit(false);
        break;
    }
  }

  public isNavDisabled(operation: 'increment' | 'decrement'): boolean {
    const resourceEventIndex = this.resourcesNavigationContext?.resourceEventIndexInGroup;
    const data: MobileResourceEventViewModel[] = this.resourcesNavigationContext?.resourceEventGroup.resourceEvents;
    if (data && data.length > 0) {
      switch (operation) {
        case 'increment':
          return data[resourceEventIndex]?.id === data[Object.keys(data).slice(-1)[0]]?.id;
        case 'decrement':
          return resourceEventIndex === this.tryGetFirstResourceEventIndex(data, 0);
      }
    }
    return true;
  }

  private tryGetFirstResourceEventIndex(data: MobileResourceEventViewModel[], index: number): number {
    if (this.resourcesNavigationContext.resourceEventGroup.resourceEvents.length > index) {
      if (CompareHelper.isEmptyObject(data[index])) {
        index++;
        return this.tryGetFirstResourceEventIndex(data, index);
      }
      return index;
    } else {
      return -1;
    }
  }
}
